/**
 * Offline Store factory.
 *
 * Persists the pending-expense queue via the supplied StorageAdapter
 * (localStorage on web, AsyncStorage on mobile).
 *
 * IMPORTANT: Call this **exactly once per app process** — see
 * createAuthStore.ts for the rationale.
 */
import { create } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import type { PendingExpense, SyncStatus } from '@splitfiesta/shared';
import { SYNC_REPEAT_PRO_REQUIRED_ERROR } from '@splitfiesta/shared';

import type { StorageAdapter } from '../platform/storage';
import { SYNC_GROUP_LIMIT_ERROR } from '../services/offline/syncFailure';

type NullableItemKeys =
  'lineItems' | 'lineItemsRest' | 'unpickedSplit' | 'lineItemParticipants';

/** `null` on a conversion or line-item key clears it (see `updatePendingExpense`). */
export type PendingExpensePatch = Partial<
  Omit<PendingExpense, 'originalCurrency' | 'exchangeRate' | NullableItemKeys>
> & {
  originalCurrency?: string | null;
  exchangeRate?: number | null;
} & {
  [K in NullableItemKeys]?: PendingExpense[K] | null;
};

/** `receiptDropped`: the server saved the row without its receipt photo (the draft had expired). */
export type SyncWarningKind = 'receiptDropped';

export type SyncWarning = {
  localId: string;
  serverId: string;
  warning: SyncWarningKind;
};

interface OfflineState {
  pendingExpenses: PendingExpense[];
  isSyncing: boolean;
  lastSyncAttempt: number | null;
  hasHydrated: boolean;
  /**
   * When the user last edited a failed item. The sync hooks re-trigger on
   * queue length, which an edit leaves unchanged, so they watch this too.
   */
  retryRequestedAt: number | null;
  /** In memory only: the indicator shows it once, and a restart forgets it. */
  lastSyncWarning: SyncWarning | null;
}

interface OfflineActions {
  addPendingExpense: (expense: PendingExpense) => void;
  updateExpenseStatus: (
    localId: string,
    status: SyncStatus,
    error?: string
  ) => void;
  /**
   * Patch any subset of fields on a pending expense. Used by the
   * sync-time cloud upgrade in `runOfflineSync` to write the
   * cloud-derived `category` / `subcategory` / source / confidence
   * back to the queue before POST, so a retry after a partial
   * failure doesn't re-upgrade.
   *
   * `originalCurrency: null` is a dedicated "clear the conversion"
   * signal (widened past `PendingExpense`'s own `string | undefined`
   * field, which has no null variant): it drops `originalAmount`,
   * `originalCurrency` AND `exchangeRate` together, rather than writing
   * `null` into fields the queued `CreateExpenseDto` shape can't hold.
   */
  /**
   * `retry` marks the edit as the user's explicit retry of a failed item.
   * The sync runner also patches items (category upgrade), and that must
   * not reset the retry budget.
   */
  updatePendingExpense: (
    localId: string,
    patch: PendingExpensePatch,
    options?: { retry?: boolean }
  ) => void;
  /**
   * Reverts every `'syncing'` item to `'pending'` and returns the resulting
   * queue. `'syncing'` only ever describes an in-flight POST, so outside one
   * (a store just restored from disk, or a sync run starting with nothing in
   * flight) it marks a process that died mid-sync — and the sync runner only
   * picks `'pending'` / `'failed'`, so the item would be stranded forever.
   * Returns the queue because a caller holding a store *snapshot* (the sync
   * runner) can't see the new array through its own reference.
   */
  reconcileStaleSyncing: () => PendingExpense[];
  markExpenseSynced: (localId: string, serverId: string) => void;
  removePendingExpense: (localId: string) => void;
  /** Drops `ownerId`'s rows for a group they deleted or left. */
  removePendingExpensesForGroup: (
    groupId: string,
    ownerId: string | undefined
  ) => void;
  /**
   * Gives `ownerId`'s rows refused for the free group expense limit a fresh
   * budget and wakes the outbox, the same way an edit-and-retry does. The
   * refusal spends the budget like any 4xx, so once the group (or, with no
   * `groupId`, the user) goes Pro nothing else would resend them.
   */
  requeueGroupLimitFailures: (
    ownerId: string | undefined,
    groupId?: string
  ) => void;
  clearPendingExpenses: () => void;
  /** Gives every row queued before `ownerId` existed to `ownerId`. */
  adoptUnownedExpenses: (ownerId: string) => void;
  setSyncing: (isSyncing: boolean) => void;
  setLastSyncAttempt: (timestamp: number) => void;
  setSyncWarning: (warning: SyncWarning) => void;
  clearSyncWarning: () => void;
  getPendingExpensesByGroup: (
    groupId: string,
    ownerId: string | undefined
  ) => PendingExpense[];
  getExpenseByLocalId: (
    localId: string,
    ownerId: string | undefined
  ) => PendingExpense | undefined;
  setHasHydrated: (state: boolean) => void;
}

export type OfflineStore = OfflineState & OfflineActions;

/** Keys whose `null` in a patch removes the stored value. */
const NULL_DELETES_KEY: ReadonlySet<string> = new Set([
  'exchangeRate',
  'subcategory',
  'lineItems',
  'lineItemsRest',
  'unpickedSplit',
  'lineItemParticipants',
]);

const isOwnedBy = (expense: PendingExpense, ownerId: string | undefined) =>
  ownerId !== undefined && expense.ownerId === ownerId;

const isGroupLimitFailure = (
  expense: PendingExpense,
  ownerId: string | undefined,
  groupId: string | undefined
) =>
  isOwnedBy(expense, ownerId) &&
  (groupId === undefined || expense.groupId === groupId) &&
  expense.syncStatus === 'failed' &&
  expense.lastError === SYNC_GROUP_LIMIT_ERROR;

/**
 * The rows queued by `ownerId`. The queue is shared by every account that
 * signs in on the device, so all reads go through this; signed out
 * (`undefined`) sees nothing.
 */
export const selectOwnedExpenses = (
  state: Pick<OfflineState, 'pendingExpenses'>,
  ownerId: string | undefined
): PendingExpense[] => state.pendingExpenses.filter(e => isOwnedBy(e, ownerId));

export const selectFailedExpenses = (
  state: Pick<OfflineState, 'pendingExpenses'>,
  ownerId: string | undefined
): PendingExpense[] =>
  selectOwnedExpenses(state, ownerId).filter(e => e.syncStatus === 'failed');

export const selectPendingCount = (
  state: Pick<OfflineState, 'pendingExpenses'>,
  ownerId: string | undefined
): number =>
  selectOwnedExpenses(state, ownerId).filter(e => e.syncStatus === 'pending')
    .length;

export interface CreateOfflineStoreDeps {
  storage: StorageAdapter;
}

export function createOfflineStore(deps: CreateOfflineStoreDeps) {
  let reconcileNextRehydrate = true;

  const store = create<OfflineStore>()(
    devtools(
      persist(
        immer((set, get) => ({
          pendingExpenses: [],
          isSyncing: false,
          lastSyncAttempt: null,
          hasHydrated: false,
          retryRequestedAt: null,
          lastSyncWarning: null,

          addPendingExpense: expense => {
            set(state => {
              state.pendingExpenses.push(expense);
            });
          },

          updateExpenseStatus: (localId, status, error) => {
            set(state => {
              const expense = state.pendingExpenses.find(
                e => e.localId === localId
              );
              if (expense) {
                expense.syncStatus = status;
                if (error) {
                  expense.lastError = error;
                }
                if (status === 'failed') {
                  expense.retryCount += 1;
                  expense.lastRetryAt = Date.now();
                }
              }
            });
          },

          updatePendingExpense: (localId, patch, options) => {
            set(state => {
              const expense = state.pendingExpenses.find(
                e => e.localId === localId
              );
              if (!expense) return;
              expense.revision = (expense.revision ?? 0) + 1;
              // Immer-friendly in-place assign; `Object.assign` keeps
              // the proxy intact. Don't allow `localId` to be patched.
              // Keys carrying `undefined` are dropped: callers spread whole
              // form objects in here, and `Object.assign` would otherwise
              // overwrite a stored value with `undefined` just because the
              // caller's shape declared the key. An explicit `null` still
              // goes through — it means "clear this".
              const { localId: _ignored, ...safePatch } = patch;
              const categoryChanged =
                (safePatch.category !== undefined &&
                  safePatch.category !== expense.category) ||
                (safePatch.subcategory !== undefined &&
                  (safePatch.subcategory ?? null) !==
                    (expense.subcategory ?? null));
              for (const [key, value] of Object.entries(safePatch)) {
                if (value === undefined) continue;
                // `originalCurrency: null` is handled below, as a
                // clear-the-whole-conversion signal — never written as a
                // literal `null` into the (non-nullable) field.
                if (key === 'originalCurrency' && value === null) continue;
                // A provider rate replacing a stored manual one: the manual
                // rate must go, not be left beside the provider's rateDate.
                // A cleared subcategory or itemization is removed rather
                // than kept as null.
                if (NULL_DELETES_KEY.has(key) && value === null) {
                  delete (expense as Record<string, unknown>)[key];
                  continue;
                }
                (expense as Record<string, unknown>)[key] = value;
              }
              // Clearing back to the group currency drops the conversion
              // entirely: a queued expense was never synced, so there is no
              // "converted, but with no rate" state to preserve.
              if (safePatch.originalCurrency === null) {
                delete expense.originalAmount;
                delete expense.originalCurrency;
                delete expense.exchangeRate;
              }
              // A patch that restates the conversion leaves no pin unless it
              // sends one: the old pin belongs to the old rate (and date),
              // and the server would ignore it out of window and reprice.
              if (
                (safePatch.originalCurrency !== undefined ||
                  safePatch.pendingConversion !== undefined) &&
                safePatch.rateDate === undefined
              ) {
                delete expense.rateDate;
              }
              // A category the user chose replaces the prediction; without
              // its metadata the sync-time cloud upgrade treats it as manual.
              if (
                categoryChanged &&
                safePatch.predictedCategorySource === undefined
              ) {
                delete expense.predictedCategorySource;
                delete expense.predictedCategoryConfidence;
              }
              // An edit that converted the item (a manual or previewed
              // rate) or switched it to the group currency leaves it in
              // its target currency: the sync runner must post it as is.
              // Keeping the flags would convert it again at rate 1 and
              // overwrite the original amount and rate with the converted
              // ones.
              if (
                expense.pendingConversion &&
                expense.currency === expense.targetCurrency
              ) {
                delete expense.pendingConversion;
                delete expense.targetCurrency;
              }
              // Automatic retries stop once the budget is spent, so without a
              // reset the corrected expense would never sync. An edit that
              // lands while the runner holds the item keeps its status (the
              // runner will set the outcome) but still earns a fresh budget
              // and a re-trigger, so the corrected row is attempted next.
              if (options?.retry) {
                // The server refused the repeat for Pro and the edit form
                // cannot show it, so an edit-and-retry means "save it
                // without the repeat"; keeping it would only fail again.
                if (expense.lastError === SYNC_REPEAT_PRO_REQUIRED_ERROR) {
                  delete expense.repeat;
                }
                expense.retryCount = 0;
                delete expense.lastError;
                delete expense.lastRetryAt;
                if (expense.syncStatus === 'failed') {
                  expense.syncStatus = 'pending';
                }
                state.retryRequestedAt = Date.now();
              }
            });
          },

          reconcileStaleSyncing: () => {
            // Skip the `set` when there is nothing to revert: persist writes
            // to storage on every `set`, and this runs on each sync attempt.
            if (get().pendingExpenses.some(e => e.syncStatus === 'syncing')) {
              set(state => {
                for (const expense of state.pendingExpenses) {
                  if (expense.syncStatus === 'syncing') {
                    expense.syncStatus = 'pending';
                  }
                }
              });
            }
            return get().pendingExpenses;
          },

          markExpenseSynced: localId => {
            set(state => {
              state.pendingExpenses = state.pendingExpenses.filter(
                e => e.localId !== localId
              );
            });
          },

          removePendingExpense: localId => {
            set(state => {
              state.pendingExpenses = state.pendingExpenses.filter(
                e => e.localId !== localId
              );
            });
          },

          removePendingExpensesForGroup: (groupId, ownerId) => {
            set(state => {
              state.pendingExpenses = state.pendingExpenses.filter(
                e => e.groupId !== groupId || !isOwnedBy(e, ownerId)
              );
            });
          },

          requeueGroupLimitFailures: (ownerId, groupId) => {
            // Skip the `set` when nothing matches: persist rewrites storage
            // on every `set`, and every Pro add lands here.
            if (
              !get().pendingExpenses.some(e =>
                isGroupLimitFailure(e, ownerId, groupId)
              )
            ) {
              return;
            }
            set(state => {
              for (const expense of state.pendingExpenses) {
                if (!isGroupLimitFailure(expense, ownerId, groupId)) continue;
                expense.syncStatus = 'pending';
                expense.retryCount = 0;
                delete expense.lastError;
                delete expense.lastRetryAt;
              }
              state.retryRequestedAt = Date.now();
            });
          },

          clearPendingExpenses: () => {
            set(state => {
              state.pendingExpenses = [];
            });
          },

          adoptUnownedExpenses: ownerId => {
            // Skip the `set` when there is nothing to adopt: persist writes
            // to storage on every `set`, and the sync hooks call this on
            // every sign-in.
            if (get().pendingExpenses.every(e => e.ownerId !== undefined)) {
              return;
            }
            set(state => {
              for (const expense of state.pendingExpenses) {
                if (expense.ownerId === undefined) expense.ownerId = ownerId;
              }
            });
          },

          setSyncing: isSyncing => {
            set(state => {
              state.isSyncing = isSyncing;
            });
          },

          setLastSyncAttempt: timestamp => {
            set(state => {
              state.lastSyncAttempt = timestamp;
            });
          },

          setSyncWarning: warning => {
            set(state => {
              state.lastSyncWarning = warning;
            });
          },

          clearSyncWarning: () => {
            set(state => {
              state.lastSyncWarning = null;
            });
          },

          getPendingExpensesByGroup: (groupId, ownerId) =>
            selectOwnedExpenses(get(), ownerId).filter(
              e => e.groupId === groupId
            ),

          getExpenseByLocalId: (localId, ownerId) =>
            selectOwnedExpenses(get(), ownerId).find(
              e => e.localId === localId
            ),

          setHasHydrated: hydrated => {
            set(state => {
              state.hasHydrated = hydrated;
            });
          },
        })),
        {
          name: 'offline-expenses-storage',
          storage: createJSONStorage(() => deps.storage),
          partialize: state => ({
            pendingExpenses: state.pendingExpenses,
            lastSyncAttempt: state.lastSyncAttempt,
          }),
          onRehydrateStorage: () => {
            // Read when the hydrate starts, not when it lands, so an
            // overlapping cold-start hydrate keeps its own answer.
            const reconcile = reconcileNextRehydrate;
            reconcileNextRehydrate = true;
            return state => {
              if (!state) return;
              // A store just restored from disk can have nothing in flight,
              // so this is the one moment where every 'syncing' item is
              // provably stale — see the action for what that means and why.
              if (reconcile) state.reconcileStaleSyncing();
              state.setHasHydrated(true);
            };
          },
        }
      ),
      { name: 'offline-store' }
    )
  );

  return Object.assign(store, {
    /**
     * Reloads the queue another tab wrote. That tab may be mid-sync, so its
     * 'syncing' rows are live and must not be reverted (and written back).
     */
    rehydrateFromOtherTab: async (): Promise<void> => {
      reconcileNextRehydrate = false;
      await store.persist.rehydrate();
    },
  });
}
